import {
  alpha,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useColorScheme,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import { HeatMapCellProps } from "../../types";
import ContextMenu from "../ContextMenu"; // ✅ Import ContextMenu
import {
  ColorConfig,
  HeatMapCell,
  ProcessedCellData,
  ProcessedRowData,
  WeeklyHeatMapProps,
} from "./WeeklyHeatMap.types";
import { getColorWithIntensity, needsWhiteText } from "./WeeklyHeatMap.utils";

function HeatMapCellComponent({ 
  cellData, 
  showValues, 
  onContextMenu 
}: HeatMapCellProps & { 
  onContextMenu?: (eventDetails: Array<{ matchId: string; targetId: string }>, description: string) => void 
}) {
  const theme = useTheme();
  const isHighlighted = cellData.isHighlighted;

  const handleContextMenu = () => {
    if (onContextMenu && cellData.eventDetails && cellData.eventDetails.length > 0) {
      onContextMenu(cellData.eventDetails, cellData.tooltipContent);
    }
  };

  // ✅ Solo mostra context menu se ci sono dati
  const hasEventDetails = cellData.eventDetails && cellData.eventDetails.length > 0;
  
  const cellComponent = (
    <Tooltip title={cellData.tooltipContent} arrow>
      <Paper
        elevation={cellData.elevation}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 32,
          minWidth: 32,
          cursor: "pointer",
          fontSize: "0.625rem",
          fontWeight: theme.typography.fontWeightBold,
          transition: theme.transitions.create(["transform", "box-shadow"], {
            duration: theme.transitions.duration.shorter,
          }),
          border: isHighlighted
            ? `3px solid ${theme.palette.error.main}`
            : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          position: "relative",
          borderRadius: 1,
          backgroundColor: cellData.backgroundColor,
          color: cellData.textColor,
          boxShadow: isHighlighted
            ? `0 0 12px ${alpha(theme.palette.error.main, 0.6)}`
            : undefined,
          "&:hover": {
            transform: "scale(1.1)",
            zIndex: 10,
            boxShadow: isHighlighted
              ? `0 6px 16px ${alpha(theme.palette.error.main, 0.8)}`
              : theme.shadows[4],
          },
          "&::after": isHighlighted
            ? {
                content: '"⭐"',
                position: "absolute",
                top: -2,
                right: -2,
                fontSize: 8,
                backgroundColor: theme.palette.error.main,
                borderRadius: "50%",
                width: 12,
                height: 12,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }
            : undefined,
        }}
      >
        {showValues && cellData.value > 0 ? cellData.displayValue : ""}
      </Paper>
    </Tooltip>
  );

  // ✅ Avvolgi con ContextMenu solo se ci sono dati
  if (hasEventDetails) {
    return (
      <ContextMenu
        items={[
          {
            label: "Vedi dettagli eventi",
            onClick: handleContextMenu,
          },
        ]}
      >
        {cellComponent}
      </ContextMenu>
    );
  }

  return cellComponent;
}

// Row component for a single day
interface HeatMapRowProps {
  /**
   * Data for the row (day)
   */
  rowData: ProcessedRowData;
  /**
   * Array of hours to display (0-23)
   */
  displayHours: number[];
  /**
   * Whether to show values inside the cells
   */
  showValues: boolean;
  /**
   * Optional array of total events per day for tooltip calculation
   */
  dayTotals?: number[];
  /**
   * Optional total number of events for percentage calculation in tooltips
   */
  totalEvents?: number;
  /**
   * Callback per context menu
   */
  onCellContextMenu?: (eventDetails: Array<{ matchId: string; targetId: string }>, description: string) => void;
}

function HeatMapRow({
  rowData,
  displayHours,
  showValues,
  dayTotals,
  totalEvents,
  onCellContextMenu,
}: HeatMapRowProps) {
  const dayTotal = dayTotals?.[rowData.dayIndex] || 0;
  let tooltip = "";

  if (dayTotal > 0) {
    const percentage =
      totalEvents && totalEvents > 0 ? (dayTotal / totalEvents) * 100 : 0;
    tooltip = `${rowData.dayLabel} - ${dayTotal} eventi (${percentage.toFixed(
      1
    )}% del totale eventi)`;
  }

  return (
    <Box sx={{ display: "contents" }}>
      {/* Day label with tooltip */}
      <Tooltip title={tooltip} arrow>
        <Paper
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "8px 4px",
            backgroundColor: "action.hover",
            fontSize: "0.6875rem",
            fontWeight: 600,
            minHeight: 32,
            minWidth: 40,
            borderRadius: 1,
            color: "text.secondary",
          }}
        >
          {rowData.dayLabel}
        </Paper>
      </Tooltip>

      {/* Hour cells for this day */}
      {rowData.cells.map((cellData, hourIndex) => (
        <HeatMapCellComponent
          key={`cell-${rowData.dayIndex}-${displayHours[hourIndex]}`}
          cellData={cellData}
          showValues={showValues}
          onContextMenu={onCellContextMenu} // ✅ Passa callback
        />
      ))}
    </Box>
  );
}

// Hour headers component
interface HourHeadersProps {
  displayHours: number[];
  hourTotals?: number[];
  totalEvents?: number;
}

function HourHeaders({
  displayHours,
  hourTotals,
  totalEvents,
}: HourHeadersProps) {
  return (
    <>
      <Box /> {/* Empty corner cell */}
      {displayHours.map((hour) => {
        const hourTotal = hourTotals?.[hour] || 0;
        let tooltip = "";

        if (hourTotal > 0) {
          const percentage =
            totalEvents && totalEvents > 0
              ? (hourTotal / totalEvents) * 100
              : 0;
          tooltip = `Ora ${hour
            .toString()
            .padStart(2, "0")}:00 - ${hourTotal} eventi (${percentage.toFixed(
            1
          )}% del totale eventi)`;
        }

        return (
          <Tooltip key={`hour-${hour}`} title={tooltip} arrow>
            <Paper
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 4px",
                backgroundColor: "action.hover",
                fontSize: "0.75rem",
                fontWeight: 600,
                minHeight: 32,
                minWidth: 32,
                borderRadius: 1,
                color: "text.secondary",
              }}
            >
              {hour.toString().padStart(2, "0")}
            </Paper>
          </Tooltip>
        );
      })}
    </>
  );
}

// Legend component
interface LegendProps {
  legend: NonNullable<WeeklyHeatMapProps["legend"]>;
  colorConfig: ColorConfig;
  theme: any;
}

function Legend({ legend, colorConfig, theme }: LegendProps) {
  return (
    <Box
      sx={{
        marginTop: 2,
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        Meno
      </Typography>
      <Box sx={{ display: "flex", gap: 0.25 }}>
        {legend.intensitySteps?.map(({ intensity, label }, i) => {
          const backgroundColor = getColorWithIntensity(
            intensity,
            colorConfig,
            theme
          );
          return (
            <Tooltip key={i} title={label} arrow>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: (theme) =>
                    `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  cursor: "help",
                  borderRadius: 0.5,
                  backgroundColor,
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Più
      </Typography>
      {legend.maxLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          {legend.maxLabel}
        </Typography>
      )}
      {legend.title && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 2, fontStyle: "italic" }}
        >
          {legend.title}
        </Typography>
      )}
    </Box>
  );
}

export function WeeklyHeatMap({
  data,
  displayHours,
  colorConfig,
  showValues = true,
  loading = false,
  emptyMessage = "Nessun dato disponibile",
  dayLabels,
  maxValue,
  dayTotals,
  hourTotals,
  totalEvents,
  onCellContextMenu, // ✅ Nuova prop per context menu
  legend,
}: WeeklyHeatMapProps) {
  const theme = useTheme();
  const { mode } = useColorScheme();

  const processedRows = useMemo<ProcessedRowData[]>(() => {
    return processHeatMapData(
      data,
      dayLabels,
      displayHours,
      maxValue,
      colorConfig,
      {
        emptyColor: theme.palette.action.hover,
        mode,
        whiteColor: theme.palette.common.white,
        textPrimary: theme.palette.text.primary,
      }
    );
  }, [data, dayLabels, displayHours, maxValue, colorConfig, mode]);

  if (loading) {
    return (
      <Stack
        direction="column"
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ height: 400 }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Caricamento dati...
        </Typography>
      </Stack>
    );
  }

  if (
    !data ||
    data.length === 0 ||
    data.every((row) => row.every((cell) => cell.value === 0))
  ) {
    return (
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{ height: 400 }}
      >
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Stack>
    );
  }

  console.log("WeeklyHeatMap Rendered with data:", {
    processedRows,
    displayHours,
  });

  return (
    <Box>
      <Paper
        elevation={1}
        sx={{
          display: "grid",
          gridTemplateRows: "repeat(7, 1fr) auto",
          gridTemplateColumns: `auto repeat(${displayHours.length}, 1fr)`,
          gap: 0.25,
          padding: 2,
          backgroundColor: "background.paper",
          borderRadius: 1,
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        {/* Render all day rows */}
        {processedRows.map((rowData) => (
          <HeatMapRow
            key={`day-${rowData.dayIndex}`}
            rowData={rowData}
            displayHours={displayHours}
            showValues={showValues}
            dayTotals={dayTotals}
            totalEvents={totalEvents}
            onCellContextMenu={onCellContextMenu} // ✅ Passa callback
          />
        ))}

        {/* Hour headers at bottom */}
        <HourHeaders
          displayHours={displayHours}
          hourTotals={hourTotals}
          totalEvents={totalEvents}
        />
      </Paper>

      {/* Legend */}
      {legend?.show && (
        <Legend legend={legend} colorConfig={colorConfig} theme={theme} />
      )}
    </Box>
  );
}

function processCellData(
  cellData: HeatMapCell,
  maxValue: number,
  colorConfig: ColorConfig,
  theme: any,
  getColorIntensity?: (value: number, maxValue: number) => number
): ProcessedCellData {
  const intensityForColoring = getColorIntensity
    ? getColorIntensity(cellData.value, maxValue)
    : maxValue > 0
    ? cellData.value / maxValue
    : 0;

  const clampedIntensity = Math.min(Math.max(intensityForColoring, 0), 1);
  const backgroundColor = getColorWithIntensity(
    clampedIntensity,
    colorConfig,
    theme
  );
  const whiteText = needsWhiteText(clampedIntensity, colorConfig);

  return {
    value: cellData.value,
    displayValue: cellData.displayValue || "",
    tooltipContent: cellData.tooltipContent || "",
    isHighlighted: cellData.isHighlighted || false,
    backgroundColor,
    textColor: whiteText ? theme.whiteColor : theme.textPrimary,
    elevation: cellData.value > 0 ? 2 : 1,
    eventDetails: cellData.eventDetails, // ✅ Includi eventDetails
  };
}

// Process all data for rendering
function processHeatMapData(
  data: HeatMapCell[][],
  dayLabels: string[],
  displayHours: number[],
  maxValue: number,
  colorConfig: ColorConfig,
  theme: any,
  getColorIntensity?: (value: number, maxValue: number) => number
): ProcessedRowData[] {
  return dayLabels.map((dayLabel, dayIndex) => ({
    dayLabel,
    dayIndex,
    cells: displayHours.map((hour) => {
      const cellData = data[dayIndex]?.[hour] || {
        value: 0,
        displayValue: "",
        tooltipContent: "",
        isHighlighted: false,
      };

      return processCellData(
        cellData,
        maxValue,
        colorConfig,
        theme,
        getColorIntensity
      );
    }),
  }));
}
